import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button
} from '@mui/material';
import {
  Gavel,
  WarningAmber,
  Copyright,
  ContactSupport,
  ArrowBack,
  Block,
  Update,
  CalendarMonth
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';

const QuickMenuCalendarTerms = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Terms and Conditions | Menu Calendar Quick View',
    description: 'Terms and conditions for Menu Calendar Quick View, a Mac menu bar calendar by Michael R. Ellis.',
    path: '/menu-calendar-quick-view-terms'
  });

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Terms and Conditions
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: September 22, 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            App Name: Menu Calendar Quick View
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Platform: macOS
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Developer: Michael R. Ellis
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Gavel sx={{ mr: 1 }} />
              Agreement
            </Typography>
            <Typography variant="body1" paragraph>
              By downloading, installing, or using Menu Calendar Quick View (the &quot;App&quot;), you agree to these Terms and Conditions. If you do not agree, do not use the App. The App is provided by Michael R. Ellis (&quot;we,&quot; &quot;us,&quot; or &quot;Developer&quot;).
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              License to Use
            </Typography>
            <Typography variant="body1" paragraph>
              We grant you a limited, non-exclusive, non-transferable, revocable license to use the App on Apple devices you own or control, solely for personal, non-commercial purposes, and in accordance with the App Store Terms of Service and these Terms.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Block sx={{ mr: 1 }} />
              Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree not to:
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
              <li>Copy, modify, reverse engineer, decompile, or attempt to extract the source code of the App, except where applicable law expressly permits</li>
              <li>Redistribute, rent, lease, sublicense, or commercially exploit the App without permission</li>
              <li>Use the App in any unlawful manner or in a way that interferes with its normal operation</li>
              <li>Use the App to access calendars, reminders, or accounts you are not authorized to view</li>
              <li>Remove or alter copyright, attribution, or proprietary notices</li>
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarMonth sx={{ mr: 1 }} />
              Your Calendar Data &amp; Accounts
            </Typography>
            <Typography variant="body1" paragraph>
              The App displays calendars, events, and reminders from your Mac through Apple&apos;s EventKit framework, and it can create or edit events and reminders at your direction. That content belongs to you and to the accounts it comes from; we claim no ownership of it. You are responsible for having the right to access the calendars and reminders you view through the App, and for what you create with it.
            </Typography>
            <Typography variant="body1" paragraph>
              The App is a viewer and quick-entry tool, not a backup service or a system of record. It does not store a copy of your events on our servers, and it cannot restore data that is deleted in Calendar, Reminders, or an upstream account such as iCloud, Google, or Exchange. Keep your own backups, and verify anything time-critical in the source calendar.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Copyright sx={{ mr: 1 }} />
              Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              The App, including its design, software, trademarks, and original content, is owned by the Developer or its licensors and is protected by intellectual property laws. The App incorporates third-party open-source components, which remain subject to their own licenses. Nothing in these Terms transfers ownership of the App or any part of it to you.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Accuracy &amp; Reliance Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              Event and reminder information shown in the App depends on macOS, on the permissions you have granted, and on the syncing behavior of your calendar providers. Items may appear late, appear in a different time zone, or not appear at all if access is revoked or a provider is unavailable. Do not rely on the App alone for appointments, deadlines, medication schedules, or other time-critical obligations.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningAmber sx={{ mr: 1 }} />
              Disclaimer of Warranties
            </Typography>
            <Typography variant="body1" paragraph>
              The App is provided &quot;as is&quot; and &quot;as available,&quot; without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the App will be uninterrupted, error-free, or free of defects, or that it will meet your specific requirements.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the maximum extent permitted by law, the Developer shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or goodwill, including missed appointments or deadlines, arising from your use of or inability to use the App. Our total liability for any claim arising out of these Terms or the App shall not exceed the amount you paid for the App (if any) in the twelve (12) months preceding the claim, or ten US dollars (US $10), whichever is greater.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Apple App Store
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms are between you and the Developer, not Apple Inc. Apple is not responsible for the App or its content. Apple has no obligation to provide maintenance or support. In the event of a failure of the App to conform to any applicable warranty, you may notify Apple, and Apple may refund the purchase price (if any) as required by App Store rules; to the maximum extent permitted by law, Apple has no other warranty obligation. Apple is a third-party beneficiary of these Terms and may enforce them against you.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our handling of information, including the calendar permissions the App requests and the analytics and crash reporting it uses, is described in the{' '}
              <RouterLink to="/menu-calendar-quick-view-privacy-policy" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Privacy Policy
              </RouterLink>
              , which is incorporated by reference into these Terms.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Update sx={{ mr: 1 }} />
              Changes &amp; Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may update these Terms by posting a revised version with a new effective date. Continued use after changes means you accept the updated Terms. We may suspend or terminate access to the App if you violate these Terms. You may stop using the App at any time by revoking its permissions or uninstalling it.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms are governed by the laws of the State of New York, USA, without regard to conflict-of-law principles, except where mandatory consumer protection laws in your jurisdiction require otherwise.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              Questions about these Terms:{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default QuickMenuCalendarTerms;
